/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SnapshotEnum, TransferModel } from '@src/modules/accounts/models'
import uuidv4 from 'uuid/v4'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import type { AccountInfo } from '@src/endpoints/services/base.service'
import {
  CloudTypeEnum,
  ValidateTypeEnum
} from '@src/endpoints/services/base.service'
import {
  CreateAccountReq,
  CreateOrganizationReq,
  DeleteAccountReq,
  DeleteOrganizationReq,
  TenantMgr,
  UpdateOrganizationReq,
  UpdateTenantAccountReq
} from '@src/endpoints/services/tenantMgr.service'
import { getTenant } from '@src/redux/actions/auth.actions'
import { toastSuccess } from '@src/utils/toast'
import type { State } from '@src/redux/configureStore'
import {
  parseCloudAccounts,
  parseCloudAccountsBeta,
  parseOrganizations,
  parseOrganizationsBeta
} from './helpers'
import type * as Models from './models'
import {
  organizationEditor,
  actions as EditorActions,
  InitialState as EditorInitialState
} from './organization.crud'
import { sourceDelete } from '@src/endpoints/openapis/saas-cam-correlated-sources'
import type { OrganizationInfoEx } from './models'

export type OrgStructures = {
  [key in CloudTypeEnum]?: Models.OrgStructure[]
}
export type SnapshotPages = { [key in SnapshotEnum]?: any }
export type CloudAccounts = { [key in CloudTypeEnum]?: Models.CloudAccount[] }

export const initialState = {
  isPending: false as boolean,
  accounts: {} as CloudAccounts,
  allAccounts: [] as CloudAccounts[],
  structures: {} as OrgStructures,
  snapshot: {} as SnapshotPages,
  lastUpdate: '' as string
}

type InitialState = typeof initialState

interface PayloadData extends Models.OrgStructure {}

interface GetDataPayload {
  isPending?: boolean
  structures: OrgStructures
  lastUpdate?: string
}

interface GetCloudAccountsPayload {
  isPending?: boolean
  accounts: CloudAccounts
}

interface UpdateOrganizationReqEx extends UpdateOrganizationReq {
  organization?: OrganizationInfoEx
}

interface CreateOrganizationReqEx extends CreateOrganizationReq {
  organization?: OrganizationInfoEx
}

interface AccountInfoEx extends AccountInfo {}

interface CreateAccountReqEx extends CreateAccountReq {
  account?: AccountInfoEx
}

type GetDataFromStore = {
  accounts: CloudAccounts
  structures: OrgStructures
  editor: EditorInitialState
}

const getDataFromStore = (getStore: () => State): GetDataFromStore => {
  const { organization } = getStore()
  const { summary, editor } = organization
  return { ...summary, editor } as GetDataFromStore
}

const getCurrentUidEditorFromStore = (getStore: () => State): string => {
  const { editor } = getDataFromStore(getStore)
  return editor?.uidEditor
}

const organizationAccounts = createSlice({
  name: 'organizationAccounts',
  initialState,
  reducers: {
    changeState: (state, action: PayloadAction<Partial<InitialState>>) =>
      ({ ...state, ...action.payload } as InitialState),
    pending: (state, action: PayloadAction<{ isPending?: boolean }>) => {
      state.isPending = action.payload.isPending
    },
    loadedData: (state, action: PayloadAction<GetDataPayload>) =>
      ({
        ...state,
        ...action.payload
      } as InitialState),
    loadedCloudAccounts: (
      state,
      action: PayloadAction<GetCloudAccountsPayload>
    ) =>
      ({
        ...state,
        ...action.payload
      } as InitialState),
    dispose: state => initialState,
    clear: state => ({ ...state, structures: initialState.structures })
  }
})

export const loadCloudAccounts =
  (cloud_type: CloudTypeEnum) => (dispatch: any, getStore: () => State) => {
    dispatch(
      organizationAccounts.actions.changeState({
        isPending: true,
        accounts: { ...initialState.accounts, [cloud_type]: [] }
      })
    )
    TenantMgr.ListCloudAccounts({ params: {}, queryVars: { cloud_type } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { account_list = [] } = response || {}
        dispatch(
          organizationAccounts.actions.loadedCloudAccounts({
            accounts: {
              [cloud_type]: parseCloudAccounts(account_list)
            },
            isPending: false
          })
        )
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const loadOrganizationsData =
  (cloud_type: CloudTypeEnum, useImprovements: boolean) =>
  (dispatch: any, getStore: () => State) => {
    if (useImprovements) {
      if (cloud_type === CloudTypeEnum.AWS) {
        dispatch(loadCloudAccountsBeta(cloud_type))
      }
      dispatch(loadOrganizationsBeta(cloud_type))
    } else {
      dispatch(loadSummaryData(cloud_type))
    }
  }

export const loadAccountsData =
  (cloud_type: CloudTypeEnum, useImprovements: boolean) =>
  (dispatch: any, getStore: () => State) => {
    if (useImprovements) {
      dispatch(loadCloudAccountsBeta(cloud_type))
    } else {
      dispatch(loadCloudAccounts(cloud_type))
    }
  }

export const loadSummaryData =
  (cloud_type: CloudTypeEnum) => (dispatch: any, getStore: () => State) => {
    if (cloud_type === CloudTypeEnum.AWS) {
      dispatch(loadCloudAccounts(cloud_type))
    }
    dispatch(
      organizationAccounts.actions.changeState({
        isPending: true,
        structures: { ...initialState.structures, [cloud_type]: [] }
      })
    )
    TenantMgr.ListCloudOrganizations({ params: {}, queryVars: { cloud_type } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { organization = [] } = response || {}
        const structure: PayloadData[] = parseOrganizations(organization)
        dispatch(
          organizationAccounts.actions.loadedData({
            structures: {
              [cloud_type]: structure
            },
            isPending: false
          })
        )
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const loadCloudAccountsBeta =
  (cloud_type: CloudTypeEnum) => (dispatch: any, getStore: () => State) => {
    dispatch(
      organizationAccounts.actions.changeState({
        isPending: true,
        accounts: { ...initialState.accounts }
      })
    )
    TenantMgr.ListCloudAccounts({ params: {}, queryVars: { cloud_type } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { account_list = [] } = response || {}
        const accounts: CloudAccounts = parseCloudAccountsBeta(account_list)
        dispatch(
          organizationAccounts.actions.loadedCloudAccounts({
            accounts,
            isPending: false
          })
        )
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const loadOrganizationsBeta =
  (cloud_type: CloudTypeEnum) => (dispatch: any, getStore: () => State) => {
    dispatch(
      organizationAccounts.actions.changeState({
        isPending: true,
        structures: { ...initialState.structures }
      })
    )
    TenantMgr.ListCloudOrganizations({ params: {}, queryVars: { cloud_type } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { organization = [] } = response || {}
        const structures: OrgStructures = parseOrganizationsBeta(organization)
        dispatch(
          organizationAccounts.actions.loadedData({
            structures,
            isPending: false,
            lastUpdate: new Date().getTime().toString()
          })
        )

        const editor = getStore()?.organization?.editor
        if (!editor) {
          return
        }
        const { isNew, model = {} as Models.Model } = editor
        if (!isNew) {
          return
        }

        const orgName = model?.name

        organization
          ?.filter(org => org.organization_name === orgName)
          .forEach(org => {
            dispatch(
              organizationEditor.actions.changeState({
                isNew,
                apiResponse: true,
                isTesting: false,
                model: {
                  ...new TransferModel(org).convertToModel(),
                  correlatedSource: model?.correlatedSource
                },
                enableScopeAccounts: true,
                validateType: ValidateTypeEnum.VALIDATE_TYPE_ORGANIZATION,
                isScopes: true,
                scopesData: {
                  search: '',
                  filter: {
                    label: 'All',
                    value: 'all'
                  },
                  select: 'allAccounts'
                },
                isOrg: true
              })
            )
          })
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const createAccount =
  (params: CreateAccountReqEx, useFF: boolean, callbackFn: () => void) =>
  (dispatch: any, getStore: () => State) => {
    const currentUIDEditor = getCurrentUidEditorFromStore(getStore)

    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.CreateAccount({
      params: { req_hdr: { tid: uuidv4() }, ...params },
      options: { signal: null }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to create account',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { accounts, editor } = getDataFromStore(getStore)
        const cloud_type = params?.account?.cloud_type
        dispatch(
          getTenant(() => {
            const { accounts } = getDataFromStore(getStore)
            const cloud_type = params?.account?.cloud_type
            if (cloud_type && (accounts[cloud_type] || editor.isNew)) {
              dispatch(
                useFF
                  ? loadAccountsData(params?.account?.cloud_type, useFF)
                  : loadCloudAccounts(params?.account?.cloud_type)
              )
            }
          })
        )
        if (
          cloud_type &&
          (accounts[cloud_type] || editor.isNew) &&
          editor?.uidEditor === currentUIDEditor
        ) {
          if (callbackFn && typeof callbackFn === 'function') {
            callbackFn()
          }
          toastSuccess('Account successfully created!')
        }
      })
      .catch((res: Response) => {
        dispatch(
          EditorActions.messages({
            title: 'Failed to create account',
            type: 'error',
            message: `Error: ${res?.status || 'unknown'}. ${
              res?.statusText || 'unknown'
            }`
          })
        )
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const createOrganization =
  (params: CreateOrganizationReqEx, useFF: boolean, callbackFn: () => void) =>
  (dispatch: any, getStore: () => State) => {
    const currentUIDEditor = getCurrentUidEditorFromStore(getStore)
    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.CreateOrganization({
      params: { req_hdr: { tid: uuidv4() }, ...params },
      options: { signal: null }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to create organization',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { structures, editor } = getDataFromStore(getStore)
        const cloud_type = params?.organization?.cloud_type
        dispatch(
          getTenant(() => {
            const { structures } = getDataFromStore(getStore)
            const cloud_type = params?.organization?.cloud_type
            if (cloud_type && (structures[cloud_type] || editor.isNew)) {
              dispatch(
                useFF
                  ? loadOrganizationsData(
                      params?.organization?.cloud_type,
                      useFF
                    )
                  : loadSummaryData(params?.organization?.cloud_type)
              )
            }
          })
        )
        if (
          cloud_type &&
          (structures[cloud_type] || editor.isNew) &&
          editor?.uidEditor === currentUIDEditor
        ) {
          if (callbackFn && typeof callbackFn === 'function') {
            callbackFn()
          }
          toastSuccess('Account successfully created!')
        }
      })
      .catch((res: Response) => {
        dispatch(
          EditorActions.messages({
            title: 'Failed to create organization',
            type: 'error',
            message: `Error: ${res?.status || 'unknown'}. ${
              res?.statusText || 'unknown'
            }`
          })
        )
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const updateAccount =
  (params: UpdateTenantAccountReq, useFF: boolean, callbackFn: () => void) =>
  (dispatch: any, getStore: () => State) => {
    const currentUIDEditor = getCurrentUidEditorFromStore(getStore)
    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.UpdateTenantAccount({
      params: { req_hdr: { tid: uuidv4() }, ...params },
      options: { signal: null }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to update account',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { accounts, editor } = getDataFromStore(getStore)
        const cloud_type = params?.account?.cloud_type
        dispatch(
          getTenant(() => {
            const { accounts } = getDataFromStore(getStore)
            const cloud_type = params?.account?.cloud_type
            if (cloud_type && accounts[cloud_type]) {
              dispatch(loadCloudAccounts(params?.account?.cloud_type))
            }
          })
        )
        if (
          cloud_type &&
          accounts[cloud_type] &&
          editor?.uidEditor === currentUIDEditor
        ) {
          if (callbackFn && typeof callbackFn === 'function') {
            callbackFn()
          }
        }
        toastSuccess('Account successfully updated!')
      })
      .catch((res: Response) => {
        dispatch(
          EditorActions.messages({
            title: 'Failed to update account',
            type: 'error',
            message: `Error: ${res?.status || 'unknown'}. ${
              res?.statusText || 'unknown'
            }`
          })
        )
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const updateOrganization =
  (params: UpdateOrganizationReqEx, useFF: boolean, callbackFn: () => void) =>
  (dispatch: any, getStore: () => State) => {
    const currentUIDEditor = getCurrentUidEditorFromStore(getStore)
    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.UpdateOrganization({
      params: { req_hdr: { tid: uuidv4() }, ...params },
      options: { signal: null }
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to update organization',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        const { organization = {} } = response || {}
        const { structures, editor } = getDataFromStore(getStore)
        const cloud_type = organization?.cloud_type
        dispatch(
          getTenant(() => {
            const { structures } = getDataFromStore(getStore)
            const cloud_type = organization?.cloud_type
            if (cloud_type && structures[cloud_type]) {
              dispatch(loadSummaryData(organization?.cloud_type))
            }
          })
        )
        if (
          cloud_type &&
          structures[cloud_type] &&
          editor?.uidEditor === currentUIDEditor
        ) {
          dispatch(
            useFF
              ? loadOrganizationsData(organization?.cloud_type, useFF)
              : loadSummaryData(organization?.cloud_type)
          )
          if (callbackFn && typeof callbackFn === 'function') {
            callbackFn()
          }
        }
        toastSuccess('Account successfully updated!')
      })
      .catch((res: Response) => {
        dispatch(
          EditorActions.messages({
            title: 'Failed to update organization',
            type: 'error',
            message: `Error: ${res?.status || 'unknown'}. ${
              res?.statusText || 'unknown'
            }`
          })
        )
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const deleteAccount =
  (
    params: DeleteAccountReq,
    cloud_type: CloudTypeEnum,
    correlatedSourceId: string
  ) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.DeleteAccount({ params, options: { signal: null } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to delete account',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        if (correlatedSourceId) {
          sourceDelete({ id: correlatedSourceId })
            .then(res => defaultHandleStatus(res, () => {}, true))
            .catch(() => {})
        }
        dispatch(
          getTenant(() => {
            const { accounts } = getDataFromStore(getStore)
            if (cloud_type && accounts[cloud_type]) {
              dispatch(loadCloudAccounts(cloud_type))
            }
          })
        )
        toastSuccess('Account successfully deleted!')
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const deleteOrganization =
  (params: DeleteOrganizationReq, correlatedSourceId: string) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(organizationAccounts.actions.pending({ isPending: true }))
    TenantMgr.DeleteOrganization({ params, options: { signal: null } })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(
              EditorActions.messages({
                title: 'Failed to delete organization',
                type: 'error',
                message: res?.message
              })
            )
            dispatch(
              organizationAccounts.actions.changeState({
                isPending: false
              })
            )
          },
          true
        )
      )
      .then(response => {
        if (correlatedSourceId) {
          sourceDelete({ id: correlatedSourceId })
            .then(res => defaultHandleStatus(res, () => {}, true))
            .catch(() => {})
        }
        dispatch(
          getTenant(() => {
            const { structures } = getDataFromStore(getStore)
            const cloud_type = params?.cloud_type
            if (cloud_type && structures[cloud_type]) {
              dispatch(loadSummaryData(params.cloud_type))
            }
          })
        )
        toastSuccess('Account successfully deleted!')
      })
      .catch((res: Response) => {
        dispatch(
          organizationAccounts.actions.changeState({
            isPending: false
          })
        )
      })
  }

export const { reducer, actions, caseReducers } = organizationAccounts
